import { type BuilderContent, Content } from "@builder.io/sdk-react/edge";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useEffect, useRef, useCallback, useState } from "react";

import {
  CUSTOM_COMPONENTS,
  configureBuilder,
} from "~/components/builder-registry";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { LoadingSpinner } from "~/components/ui/loading-spinner";
import { useConsent } from "~/contexts/consent";
import { transformationStoriesInfiniteQueryOptions } from "~/lib/functions/transformation-story";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";
import { clientEnv } from "~/lib/utils/client-env";

import { Heading } from "../../../components/ui/heading";

import { StoryCard, StoryCardSkeleton } from "./-components/story-card";

// Generate a stable seed for the session (client-side only)
function useSessionSeed(): number {
  const [seed, setSeed] = useState(42);

  useEffect(() => {
    const stored = sessionStorage.getItem("stories-seed");
    if (stored) {
      setSeed(parseInt(stored, 10));
    } else {
      const newSeed = Math.floor(Math.random() * 1000000);
      sessionStorage.setItem("stories-seed", newSeed.toString());
      setSeed(newSeed);
    }
  }, []);

  return seed;
}

const BUILDER_MODEL = "section";
const SECTION_ID_HEADER = "stories";
const SECTION_ID_BOTTOM = "stories-bottom";

const CANONICAL_URL = `${BRAND_URL}/stories`;

export const Route = createFileRoute("/_public/stories/")({
  staleTime: 6 * 60 * 60 * 1000, // 6 hours
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps }) => {
    const seed = 42; // Use stable seed for SSR

    const [{ cachedFetchEntries }, { getBuilderSearchParams }] =
      await Promise.all([
        import("~/lib/builder/cache"),
        import("@builder.io/sdk-react/edge"),
      ]);

    const [, sections] = await Promise.all([
      context.queryClient.ensureInfiniteQueryData(
        transformationStoriesInfiniteQueryOptions(seed),
      ),
      cachedFetchEntries({
        model: BUILDER_MODEL,
        apiKey: clientEnv.VITE_BUILDER_API_KEY,
        query: {
          "data.sectionId": { $in: [SECTION_ID_HEADER, SECTION_ID_BOTTOM] },
        },
        includeUnpublished: false,
        options: getBuilderSearchParams(deps.search),
      }),
    ]);

    const headerContent =
      sections.find((s) => s.data?.sectionId === SECTION_ID_HEADER) ?? null;
    const bottomContent =
      sections.find((s) => s.data?.sectionId === SECTION_ID_BOTTOM) ?? null;

    return { headerContent, bottomContent };
  },
  head: () => ({
    ...createBrandMeta({
      title: "Transformation Stories",
      description:
        "Real stories from real people whose lives have been transformed through coaching — finding clarity, overcoming challenges, and unlocking their potential.",
      ogType: "website",
      canonical: CANONICAL_URL,
      keywords: [
        "transformation stories",
        "coaching testimonials",
        "life coaching success",
        "personal development",
        "coaching results",
        "client testimonials",
      ],
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Transformation Stories", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  component: StoriesPage,
});

function StoriesPage() {
  const seed = useSessionSeed();
  const { headerContent, bottomContent } = Route.useLoaderData() as {
    headerContent: BuilderContent | null;
    bottomContent: BuilderContent | null;
  };
  const { allowed } = useConsent();

  useEffect(() => {
    configureBuilder();
  }, []);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      ...transformationStoriesInfiniteQueryOptions(seed),
      placeholderData: keepPreviousData,
    });

  const stories = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "200px",
      threshold: 0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [handleIntersection]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-20 -left-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-amber-100/40 via-orange-50/30 to-transparent blur-3xl" />
        <div className="absolute top-[40%] -right-40 h-[500px] w-[500px] rounded-full bg-gradient-to-bl from-purple-100/30 via-pink-50/20 to-transparent blur-3xl" />
        <div className="absolute bottom-20 -left-20 h-[400px] w-[400px] rounded-full bg-gradient-to-tr from-teal-100/20 via-emerald-50/10 to-transparent blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pt-6 pb-16 sm:px-6 md:pt-8 lg:px-8">
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Transformation Stories" },
          ]}
          isBold={false}
          className="mb-10"
        />

        {/* Header Section - Builder.io editable */}
        {headerContent ? (
          <Content
            model={BUILDER_MODEL}
            content={headerContent}
            apiKey={clientEnv.VITE_BUILDER_API_KEY}
            customComponents={CUSTOM_COMPONENTS}
            canTrack={allowed.analytics}
          />
        ) : (
          <Heading
            preHeadline="Real People, Real Change"
            headline="Transformation Stories"
            subheadline="Every journey begins with a single step. These are the stories of people who took that step and discovered their potential through the power of coaching."
            isH1
          />
        )}

        {/* Stories Grid - Masonry-style layout */}
        {isLoading ? (
          <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <StoryCardSkeleton key={i} index={i} />
            ))}
          </div>
        ) : (
          <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
            {stories.map((story, index) => (
              <StoryCard key={story.id} story={story} index={index} />
            ))}
          </div>
        )}

        {/* Load more trigger */}
        <div ref={loadMoreRef} className="mt-12 flex justify-center">
          {isFetchingNextPage && (
            <div className="flex items-center gap-3 text-gray-500">
              <LoadingSpinner className="h-5 w-5" />
              <span className="text-sm">Loading more stories...</span>
            </div>
          )}
        </div>

        {/* End message */}
        {!hasNextPage && stories.length > 0 && (
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-400">
              You've reached the end of the stories
            </p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && stories.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-lg text-gray-500">
              No stories yet. Check back soon!
            </p>
          </div>
        )}

        {/* Bottom Section - Builder.io editable */}
        {bottomContent && (
          <Content
            model={BUILDER_MODEL}
            content={bottomContent}
            apiKey={clientEnv.VITE_BUILDER_API_KEY}
            customComponents={CUSTOM_COMPONENTS}
            canTrack={allowed.analytics}
          />
        )}
      </div>
    </div>
  );
}
